import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Check, Instagram, Linkedin } from 'lucide-react'

import Header from '@/components/partners/Header'
import Footer from '@/components/partners/Footer'
import { Button } from '@/components/ui/button-partners'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { preRegisterInfluencer } from '@/services/http/partners/preRegister'

type FormValues = {
  name: string
  email: string
  email_confirmation: string
  phone: string
  password: string
  password_confirmation: string
  accepted_terms_of_use: boolean
}

const BENEFITS = [
  'Cadastro rápido e gratuito',
  'Criação de cupons personalizados',
  'Participação automática em cada venda',
  'Relatórios completos para acompanhar seus ganhos',
] as const

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RX = /^[+]?[0-9()\s+-]{8,}$/

const normIG = (v: string) =>
  v
    .trim()
    .replace(/^@/, '')
    .replace(/^https?:\/\/(www\.)?instagram\.com\//, '')
    .replace(/\/$/, '')
const normLI = (v: string) =>
  v
    .trim()
    .replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//, '')
    .replace(/^@/, '')
    .replace(/\/$/, '')
const normTT = (v: string) =>
  v
    .trim()
    .replace(/^@/, '')
    .replace(/^https?:\/\/(www\.)?tiktok\.com\/@/, '')
    .replace(/\/$/, '')

export default function Cadastro() {
  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const navigate = useNavigate()

  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [instagram, setInstagram] = useState('')
  const [linkedin, setLinkedin] = useState('')
  const [tiktok, setTiktok] = useState('')
  const [otherNetwork, setOtherNetwork] = useState('')

  const [openSuccess, setOpenSuccess] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const password = watch('password')
  const password_confirmation = watch('password_confirmation')
  const emailVal = watch('email')

  useEffect(() => {
    if (password_confirmation) trigger('password_confirmation')
    if (watch('email_confirmation')) trigger('email_confirmation')
  }, [password, password_confirmation, emailVal, trigger, watch])

  const onSubmit = async (data: FormValues) => {
    setSubmitting(true)
    try {
      const ig = normIG(instagram)
      const li = normLI(linkedin)
      const tk = normTT(tiktok)
      const other = otherNetwork.trim()

      const social_networks = [
        ig && {
          platform: 'instagram' as const,
          handle: ig,
          url: `https://instagram.com/${ig}`,
        },
        li && {
          platform: 'linkedin' as const,
          handle: li,
          url: `https://linkedin.com/in/${li}`,
        },
        tk && {
          platform: 'tiktok' as const,
          handle: tk,
          url: `https://tiktok.com/@${tk}`,
        },
        other && { platform: 'other' as const, handle: other, url: other },
      ].filter(Boolean) as Array<{
        platform: 'instagram' | 'linkedin' | 'tiktok' | 'other'
        handle: string
        url: string
      }>

      const payload = {
        influencer: {
          accepted_terms_of_use: true,
          name: data.name.trim(),
          email: data.email.trim(),
          phone: data.phone.trim(),
          password: data.password,
          password_confirmation: data.password_confirmation,
          social_networks,
        },
      }

      const result = await preRegisterInfluencer(payload)

      if (result?.errors?.length) {
        const first = result.errors[0]
        throw new Error(first?.detail || first?.title || 'Erro ao cadastrar.')
      }

      setOpenSuccess(true)
    } catch (err: any) {
      console.error('Erro inesperado:', err)
      setErrorMsg(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.[0]?.detail ||
          err?.message ||
          'Erro inesperado ao cadastrar.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleRetry = () => {
    if (submitting) return
    setErrorMsg(null)
    handleSubmit(onSubmit)()
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <section className="w-full py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
              {/* Left column - Benefits */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-8 lg:sticky lg:top-40"
              >
                <div className="space-y-2">
                  <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight">
                    <span className="block">COMECE A</span>
                    <span className="block">GANHAR COM SUA</span>
                    <span className="block italic text-primary">
                      influência
                    </span>
                  </h1>
                </div>

                <p className="text-lg text-muted-foreground font-sans leading-relaxed">
                  Cadastre-se gratuitamente e receba um painel exclusivo para
                  criar cupons, oferecer descontos aos seus seguidores e receber
                  comissões automáticas a cada venda.
                </p>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold font-sans uppercase tracking-wider text-foreground">
                    Benefícios:
                  </h3>
                  <ul className="space-y-3">
                    {BENEFITS.map(text => (
                      <li key={text} className="flex items-start gap-3">
                        <div className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-muted-foreground font-sans">
                          {text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              {/* Right column - Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="bg-card rounded-2xl p-8 md:p-10 shadow-lg border border-border">
                  <h2 className="font-serif text-2xl md:text-3xl text-foreground mb-2">
                    Conte-nos um
                    <br />
                    <span className="italic text-primary">
                      pouco sobre você!
                    </span>
                  </h2>

                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="mt-8 space-y-5"
                  >
                    {/* Nome */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium font-sans text-foreground">
                        Nome completo
                      </label>
                      <Input
                        {...register('name', {
                          required: 'Nome é obrigatório.',
                          minLength: {
                            value: 3,
                            message: 'Mínimo de 3 caracteres.',
                          },
                        })}
                        placeholder="Seu nome completo"
                      />
                      {errors.name && (
                        <p className="text-xs text-destructive font-sans">
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium font-sans text-foreground">
                        Seu e-mail
                      </label>
                      <Input
                        type="email"
                        {...register('email', {
                          required: 'E-mail é obrigatório.',
                          pattern: {
                            value: EMAIL_RX,
                            message: 'E-mail inválido.',
                          },
                        })}
                        placeholder="email@exemplo.com"
                      />
                      {errors.email && (
                        <p className="text-xs text-destructive font-sans">
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    {/* Confirmar email */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium font-sans text-foreground">
                        Confirme seu e-mail
                      </label>
                      <Input
                        type="email"
                        {...register('email_confirmation', {
                          required: 'Confirmação é obrigatória.',
                          validate: v =>
                            v === watch('email') || 'Os e-mails não coincidem.',
                        })}
                        placeholder="Confirme seu e-mail"
                      />
                      {errors.email_confirmation && (
                        <p className="text-xs text-destructive font-sans">
                          {errors.email_confirmation.message}
                        </p>
                      )}
                    </div>

                    {/* Telefone */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium font-sans text-foreground">
                        Seu número
                      </label>
                      <Input
                        {...register('phone', {
                          required: 'Número é obrigatório.',
                          pattern: {
                            value: PHONE_RX,
                            message: 'Número inválido.',
                          },
                        })}
                        placeholder="(00) 00000-0000"
                      />
                      {errors.phone && (
                        <p className="text-xs text-destructive font-sans">
                          {errors.phone.message}
                        </p>
                      )}
                    </div>

                    {/* Redes sociais */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium font-sans text-foreground">
                        Rede social{' '}
                        <span className="text-muted-foreground font-normal">
                          (opcional)
                        </span>
                      </label>
                      <div className="space-y-2.5">
                        <div className="flex items-center gap-2">
                          <Instagram className="w-5 h-5 text-muted-foreground shrink-0" />
                          <Input
                            value={instagram}
                            onChange={e => setInstagram(e.target.value)}
                            placeholder="Nome de usuário (Instagram)"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Linkedin className="w-5 h-5 text-muted-foreground shrink-0" />
                          <Input
                            value={linkedin}
                            onChange={e => setLinkedin(e.target.value)}
                            placeholder="Nome de usuário (LinkedIn)"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <svg
                            viewBox="0 0 24 24"
                            className="w-5 h-5 text-muted-foreground shrink-0"
                            fill="currentColor"
                          >
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.86a8.28 8.28 0 0 0 4.84 1.55V6.96a4.84 4.84 0 0 1-1.08-.27z" />
                          </svg>
                          <Input
                            value={tiktok}
                            onChange={e => setTiktok(e.target.value)}
                            placeholder="Nome de usuário (TikTok)"
                          />
                        </div>
                        <Input
                          value={otherNetwork}
                          onChange={e => setOtherNetwork(e.target.value)}
                          placeholder="Outra rede social"
                        />
                      </div>
                    </div>

                    {/* Senha */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium font-sans text-foreground">
                          Senha
                        </label>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            {...register('password', {
                              required: 'Senha é obrigatória.',
                              minLength: {
                                value: 6,
                                message: 'Mínimo de 6 caracteres.',
                              },
                            })}
                            placeholder="Sua senha"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        {errors.password && (
                          <p className="text-xs text-destructive font-sans">
                            {errors.password.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-sm font-medium font-sans text-foreground">
                          Confirmar senha
                        </label>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            {...register('password_confirmation', {
                              required: 'Confirmação é obrigatória.',
                              validate: v =>
                                v === password || 'As senhas não conferem.',
                            })}
                            placeholder="Confirme sua senha"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        {errors.password_confirmation && (
                          <p className="text-xs text-destructive font-sans">
                            {errors.password_confirmation.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Termos */}
                    <div className="space-y-1.5">
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          {...register('accepted_terms_of_use', {
                            required: 'É necessário aceitar os termos.',
                          })}
                          className="mt-1 h-4 w-4 rounded border-primary accent-primary"
                        />
                        <label className="text-sm font-sans text-muted-foreground leading-snug">
                          Li e aceito os{' '}
                          <a
                            href="https://fotolivro.store/termos-e-condicoes"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline font-medium"
                          >
                            Termos de uso
                          </a>{' '}
                          e{' '}
                          <a
                            href="https://fotolivro.store/privacidade"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline font-medium"
                          >
                            Política de Privacidade
                          </a>
                        </label>
                      </div>
                      {errors.accepted_terms_of_use && (
                        <p className="text-xs text-destructive font-sans">
                          {errors.accepted_terms_of_use.message}
                        </p>
                      )}
                    </div>

                    {/* Submit */}
                    <Button
                      type="submit"
                      size="lg"
                      disabled={submitting}
                      className="w-full rounded-full text-base"
                    >
                      {submitting ? 'Enviando…' : 'Criar minha conta'}
                    </Button>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Success Dialog */}
      <Dialog open={openSuccess} onOpenChange={setOpenSuccess}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              Seu cadastro foi concluído!
            </DialogTitle>
            <DialogDescription className="font-sans">
              Recebemos suas informações e vamos analisar seu perfil.
              <br />
              Você receberá a resposta por e-mail em breve.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => navigate('/')}>
              Voltar à tela principal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={!!errorMsg} onOpenChange={() => setErrorMsg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              Não foi possível concluir o cadastro
            </DialogTitle>
            <DialogDescription className="font-sans">
              {errorMsg}
              <br />
              Tente novamente em instantes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setErrorMsg(null)}>
              Fechar
            </Button>
            <Button onClick={handleRetry}>Tentar novamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
